import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { Subscribe } from "unstated";
import TemplateFormSectionModel from '../../../../shared/models/templating/api/TemplateFormSectionModel';
import TemplateFormFieldModel from '../../../../shared/models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldValueModel from '../../../../shared/models/templating/api/TemplateFormFieldValueModel';
import CheckboxField from '../../../../shared/components/forms/CheckboxField';
import TextField from '../../../../shared/components/forms/TextField';
import TextAreaField from '../../../../shared/components/forms/TextAreaField';
import SsmlField from '../../../../shared/components/forms/SsmlField/SsmlField';
import SelectField from '../../../../shared/components/forms/SelectField';
import CheckboxGroup from '../../../../shared/components/forms/CheckboxGroup';
import RadioGroup from '../../../../shared/components/forms/RadioGroup';
import MediaContainer from '../../../../shared/state/containers/MediaContainer';
import FileUploadField from '../../../../shared/components/forms/FileUploadField';
import FormFieldCard from '../../../../shared/components/forms/FormFieldCard';
import PanelContainer from '../../../../shared/components/structure/Panels/PanelContainer';
import TimeRangeField from '../../../../shared/components/forms/TimeRangeField';
import SingleDayTimeRangeField from '../../../../shared/components/forms/SingleDayTimeRangeField';
import SpecialDateField from '../../../../shared/components/forms/SpecialDateField';
import ScrollablePanelBody from '../../../../shared/components/structure/ScrollablePanelBody';
import { color_shades_dark, color_shades_lighter } from '../../../../shared/constants/colors';
import Switch from '../../../../shared/components/forms/Switch';
import Button from '../../../../shared/components/general/Button';
import AppliedApplicationTemplateContainer from '../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import styled from '@emotion/styled';
import TemplateInstructionsPanel from './TemplateInstructionsPanel';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import TemplateSaveChangesModal from '../../../../shared/components/templating/TemplateSaveChangesModal';
import { getVisibleFields } from '../../../../shared/utils/sectionUtils';
import ApplicationContainer from '../../../../shared/state/containers/ApplicationContainer';
import { isDeprecatedSingleDayTimeRangeField, isDeprecatedSpecialDateField } from '../../../../shared/utils/fieldTypeUtils';
import { evaluateConditionalDefault } from '../../../../shared/utils/conditionalDefaultUtils';
import { splitOvernightValue, mergeOvernightPrefix, extractOvernightPrefix, reconstructOvernightDisplay } from '../../../../shared/utils/weeklyScheduleFormatters';
import ApplicationVariablesContainer from '../../../../shared/hooks/ApplicationVariablesContainer';
import SaveFieldVariationModal from '../../../../shared/components/forms/SaveFieldVariationModal';
import ViewFieldVariationsModal from '../../../../shared/components/forms/ViewFieldVariationsModal';
import ApplicationVariable from '../../../../shared/models/applications/ApplicationVariable';
import PhoneNumberFormField from '../../../../shared/components/forms/PhoneNumberFormField';
import { findFieldValueForField } from '../../../../shared/utils/templateFieldMatching';

interface AppliedApplicationTemplateFormProps {
    section: TemplateFormSectionModel
    onFieldValueChange: (field: TemplateFormFieldModel, value: string) => void
    /** Atomic multi-field update — applies all updates in one setFieldValue call to avoid stale-closure races. */
    onMultipleFieldValuesChange?: (updates: Array<{ field: TemplateFormFieldModel, value: string }>) => void
    applicationId: string
    values?: TemplateFormFieldValueModel[]
    fieldsOnly?: boolean
    preview?: boolean
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    appliedAppTemplate: AppliedApplicationTemplateModel
    combinedRequiredFields: TemplateFormFieldModel[]
    completeFields: TemplateFormFieldModel[]
    submitForm: () => void
    setFieldValue: (fieldName: string, value: string) => void
    onPrevious: () => void
    recentlyAddedModuleFormFields?: TemplateFormFieldModel[]
    showAdminFields?: boolean
    templateFormValues?: TemplateFormFieldValueModel[]
    appContainer: ApplicationContainer
    onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
    /** When true, Save / Next buttons are disabled because one or more fields have validation errors. */
    hasValidationErrors?: boolean
    /** All fields across all sections in the form, used to evaluate cross-section conditional defaults. */
    allFormFields?: TemplateFormFieldModel[]
}

const AppliedApplicationTemplateSectionForm: React.FC<AppliedApplicationTemplateFormProps> = (props) => {
    const variablesContainer = ApplicationVariablesContainer.useContainer();

    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    const [saveVariationModalVisible, setSaveVariationModalVisible] = useState(false);
    const [viewVariationsModalVisible, setViewVariationsModalVisible] = useState(false);
    const [selectedField, setSelectedField] = useState<TemplateFormFieldModel | null>(null);

    useEffect(() => {
        if (props.applicationId) {
            variablesContainer.loadApplicationVariablesByApplicationId(props.applicationId);
        }
    }, [props.applicationId]);

    const isRecentlyAddedModuleFormField = (field: TemplateFormFieldModel) => {
        return props.recentlyAddedModuleFormFields?.some(f => f.id == field.id);
    };

    const getValue = (field: TemplateFormFieldModel) => {
        const foundField = field?.name
            ? props.values?.find(v => v?.dynamicFormFieldName == field?.name)
            : findFieldValueForField(field, props.values);
        // If there is no stored field value record at all, return undefined so
        // getEffectiveDefault / getDisplayValue can fall back to the conditional
        // or static default.  Once a record exists, return its value verbatim
        // (including empty string) so the user can clear a field completely
        // without it snapping back to the default on the last keystroke.
        if (!foundField) return undefined;
        const val = foundField.value;
        // Guard against the "\n" artefact a blank contentEditable div can emit.
        if (val === null || val === undefined || val === '\n') return undefined;
        return val;
    };

    /**
     * Returns the effective default value for a field: evaluates any
     * conditionalDefaults first, then falls back to the static defaultValue.
     */
    const getEffectiveDefault = (field: TemplateFormFieldModel): string | undefined => {
        const allFields = props.allFormFields ?? props.section?.templateFormFields ?? [];
        return evaluateConditionalDefault(field, allFields, props.values ?? []);
    };

    /**
     * Returns the value to display for a field.
     *
     * The stored user-supplied value always takes precedence. The effective
     * default (conditional or static) is only used as a fallback when no
     * stored value exists, ensuring fields with conditional defaults remain
     * editable at all times.
     */
    const getDisplayValue = (field: TemplateFormFieldModel): string | undefined => {
        return getValue(field) ?? getEffectiveDefault(field);
    };

    const getFieldName = (field: TemplateFormFieldModel): string => {
        return field.name ?? field.id;
    };

    const handleChange = (field: TemplateFormFieldModel, value) => {
        props.onFieldValueChange(field, value);
    };

    const handleReset = (field: TemplateFormFieldModel) => {
        const effectiveDefault = getEffectiveDefault(field);
        if (effectiveDefault !== undefined) {
            props.onFieldValueChange(field, effectiveDefault);
        }
    };

    const getFieldVariations = (field: TemplateFormFieldModel): ApplicationVariable[] => {
        const prefix = `Form_Field_${field.title}_`;
        return variablesContainer.applicationVariables.filter(v => v.name.startsWith(prefix));
    };

    const hasFieldVariations = (field: TemplateFormFieldModel): boolean => {
        return getFieldVariations(field).length > 0;
    };

    const handleOpenSaveVariation = (field: TemplateFormFieldModel) => {
        setSelectedField(field);
        setSaveVariationModalVisible(true);
    };

    const handleOpenViewVariations = (field: TemplateFormFieldModel) => {
        setSelectedField(field);
        setViewVariationsModalVisible(true);
    };



    const handleSelectVariation = (variation: ApplicationVariable) => {
        if (selectedField) {
            props.onFieldValueChange(selectedField, variation.value);
        }
        setViewVariationsModalVisible(false);
        setSelectedField(null);
    };

    // ONly show the variantions for these
    const supportsVariations = (field: TemplateFormFieldModel): boolean => {
        const textBasedFieldTypes = [
            "ShortTextField",
            "NumberTextField",
            "DateTextField",
            "LongTextField",
            "SSMLField",
            "SSMLTextField"
        ];
        return textBasedFieldTypes.includes(field.fieldType);
    };

    const renderCheckbox = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <CheckboxField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    checked={getValue(field) == 'true'}
                    onChange={(e) => handleChange(field, e.target.checked.toString())}
                    className={checkboxField} />
            </div>
        )
    };

    const renderSwitch = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <Switch
                    className={switchContainer}
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    onChange={(checked) => handleChange(field, checked.toString())}
                    checked={getValue(field) == 'true'}
                    label={field.label}
                />
            </div>
        )
    };

    const renderShortText = (field: TemplateFormFieldModel, type?: string) => {
        if (isDeprecatedSingleDayTimeRangeField(field, props.appContainer, props.appliedAppTemplate)) {
            const siblings = getSingleDayTimeRangeSiblings(field);
        const siblingDayLabels = siblings.map(f => f.title ?? f.label ?? '').filter(Boolean);
            return (
                <SingleDayTimeRangeField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getFieldDisplayValue(field)}
                    defaultValue={getEffectiveDefault(field)}
                    required={field.isRequired}
                    onChange={(value) => handleSingleDayTimeRangeChange(field, value)}
                    onBlur={() => { }}
                    onReset={() => handleReset(field)}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                    siblingDayLabels={siblingDayLabels}
                    onCopyToOtherDays={(targetLabels, valueToApply) => handleCopyToSiblingDays(field, targetLabels, valueToApply)}
                />
            )
        }

        if (isDeprecatedSpecialDateField(field, props.appContainer, props.appliedAppTemplate)) {
            return (
                <SpecialDateField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getDisplayValue(field)}
                    defaultValue={getEffectiveDefault(field)}
                    onChange={(value) => handleChange(field, value)}
                    onBlur={() => { }}
                    onReset={() => handleReset(field)}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                />
            )
        }

        return (
            <TextField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                type={type}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder} />
        )
    };



    const renderPhoneNumber = (field: TemplateFormFieldModel) => {
        return (
            <PhoneNumberFormField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        );
    };

    const renderLongText = (field: TemplateFormFieldModel) => {
        return (
            <TextAreaField
                showResponseVariableTest
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                onChange={(e) => handleChange(field, e.target.value)}
                defaultValue={getEffectiveDefault(field)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder} />
        )
    };

    const renderSsmlField = (field: TemplateFormFieldModel) => {
        return (
            <SsmlField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field) ?? ''}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                applicationId={props.applicationId} />
        )
    };

    const renderSelect = (field: TemplateFormFieldModel) => {
        return (
            <SelectField
                key={field.id ?? field.name}
                className={selectField}
                onChange={(e: any) => handleChange(field, e.value)}
                onBlur={() => { }}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
            />
        )
    };

    const renderCheckboxGroup = (field: TemplateFormFieldModel) => {
        // note: checkboxes use pipe delimited values from the core value
        return (
            <CheckboxGroup
                key={field.id ?? field.name}
                onChange={(e, c) => {
                    var val = getDisplayValue(field);
                    if (c) {
                        val += `|${e.value}`;
                    } else {
                        val = val.replace(e.value, '');
                    }
                    handleChange(field, val)
                }}
                values={(getDisplayValue(field))?.split('|')}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
            />
        )
    };

    const renderRadioGroup = (field: TemplateFormFieldModel) => {
        return (
            <RadioGroup
                key={field.id ?? field.name}
                onChange={(e) => handleChange(field, e.value)}
                value={getDisplayValue(field)}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
            />
        )
    };

    const renderFileUpload = (field: TemplateFormFieldModel) => {
        return (
            <Subscribe to={[MediaContainer]}>
                {(mediaContainer: MediaContainer) => (
                    <FileUploadField
                        onChange={(e) => handleChange(field, e.url)}
                        disabled={false}
                        field={field}
                        mediaContainer={mediaContainer}
                        applicationId={props.applicationId}
                        value={getValue(field)}
                        preview={props.preview}
                    />
                )}
            </Subscribe>
        )
    };

    const renderTimeRange = (field: TemplateFormFieldModel) => {
        return (
            <TimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
            />
        )
    };

    const getSingleDayTimeRangeSiblings = (field: TemplateFormFieldModel): TemplateFormFieldModel[] => {
        return (props.section?.templateFormFields ?? []).filter(
            f => f.id !== field.id &&
                (f.fieldType === 'SingleDayTimeRangeField' ||
                    isDeprecatedSingleDayTimeRangeField(f, props.appContainer, props.appliedAppTemplate))
        );
    };

    /**
     * Canonical Mon→Sun variable order used to find the "next day" field.
     * Must match the uppercase values used in SINGLE_DAY_TIME_RANGE_FIELD_ASSOCIATED_VARIABLES
     * in fieldTypeUtils.ts (i.e. the actual associatedVariable values stored by the backend).
     */
    const DAY_VARIABLE_ORDER = [
        '{MONDAY}', '{TUESDAY}', '{WEDNESDAY}', '{THURSDAY}',
        '{FRIDAY}', '{SATURDAY}', '{SUNDAY}',
    ];

    const getNextDaySiblingField = (field: TemplateFormFieldModel): TemplateFormFieldModel | null => {
        const allDayFields = [field, ...getSingleDayTimeRangeSiblings(field)];

        const varIndex = DAY_VARIABLE_ORDER.indexOf(field.associatedVariable);
        if (varIndex !== -1) {
            const nextVar = DAY_VARIABLE_ORDER[(varIndex + 1) % DAY_VARIABLE_ORDER.length];
            const byVar = allDayFields.find(f => f.associatedVariable === nextVar);
            if (byVar) return byVar;
        }

        const sectionFields = props.section?.templateFormFields ?? [];
        const posIndex = sectionFields.findIndex(f => f.id === field.id || f.name === field.name);
        if (posIndex !== -1 && posIndex + 1 < sectionFields.length) {
            const candidate = sectionFields[posIndex + 1];
            if (
                candidate.fieldType === 'SingleDayTimeRangeField' ||
                isDeprecatedSingleDayTimeRangeField(candidate, props.appContainer, props.appliedAppTemplate)
            ) {
                return candidate;
            }
        }

        return null;
    };

    const getPreviousDaySiblingField = (field: TemplateFormFieldModel): TemplateFormFieldModel | null => {
        const allDayFields = [field, ...getSingleDayTimeRangeSiblings(field)];

        const varIndex = DAY_VARIABLE_ORDER.indexOf(field.associatedVariable);
        if (varIndex !== -1) {
            const prevVar = DAY_VARIABLE_ORDER[(varIndex - 1 + DAY_VARIABLE_ORDER.length) % DAY_VARIABLE_ORDER.length];
            const byVar = allDayFields.find(f => f.associatedVariable === prevVar);
            if (byVar) return byVar;
        }

        const sectionFields = props.section?.templateFormFields ?? [];
        const posIndex = sectionFields.findIndex(f => f.id === field.id || f.name === field.name);
        if (posIndex > 0) {
            const candidate = sectionFields[posIndex - 1];
            if (
                candidate.fieldType === 'SingleDayTimeRangeField' ||
                isDeprecatedSingleDayTimeRangeField(candidate, props.appContainer, props.appliedAppTemplate)
            ) {
                return candidate;
            }
        }

        return null;
    };

    const handleSingleDayTimeRangeChange = (field: TemplateFormFieldModel, value: string) => {
        const { currentDay, nextDay } = splitOvernightValue(value);

        // Re-apply any overnight prefix from the previous day that was stored in
        // this field.  getFieldDisplayValue strips this prefix before showing it
        // in the UI, so the component returns a clean value — we must put the
        // prefix back when writing to preserve the previous day's overnight
        // display reconstruction.
        const rawStoredValue = getValue(field);
        const prevField = getPreviousDaySiblingField(field);
        const prevFieldRawValue = prevField ? getDisplayValue(prevField) : undefined;
        const { prefix: prevDayPrefix } = extractOvernightPrefix(rawStoredValue, prevFieldRawValue);
        const currentDayValue = prevDayPrefix
            ? mergeOvernightPrefix(prevDayPrefix, currentDay)
            : currentDay;

        const nextField = getNextDaySiblingField(field);
        if (nextDay !== null) {
            if (nextField) {
                const nextFieldCurrentValue = getDisplayValue(nextField);
                const mergedNextValue = mergeOvernightPrefix(nextDay, nextFieldCurrentValue);
                if (props.onMultipleFieldValuesChange) {
                    props.onMultipleFieldValuesChange([
                        { field, value: currentDayValue },
                        { field: nextField, value: mergedNextValue },
                    ]);
                } else {
                    props.onFieldValueChange(field, currentDayValue);
                    props.onFieldValueChange(nextField, mergedNextValue);
                }
                return;
            }
        } else if (nextField) {
            // No overnight overflow any more — strip any previously-written
            // overnight prefix from the next-day field so stale "00:00,HH:MM"
            // periods don't persist across edits.
            const nextFieldRaw = getDisplayValue(nextField);
            const { prefix: stalePrefix, remainder: cleanedNextValue } = extractOvernightPrefix(nextFieldRaw, currentDayValue);
            if (stalePrefix) {
                if (props.onMultipleFieldValuesChange) {
                    props.onMultipleFieldValuesChange([
                        { field, value: currentDayValue },
                        { field: nextField, value: cleanedNextValue },
                    ]);
                } else {
                    props.onFieldValueChange(field, currentDayValue);
                    props.onFieldValueChange(nextField, cleanedNextValue);
                }
                return;
            }
        }
        props.onFieldValueChange(field, currentDayValue);
    };

    /**
     * Returns the value to pass to a SingleDayTimeRangeField for display
     * purposes.  This is a read-time complement to the write-time split/merge:
     *
     *   1. Strip any overnight prefix the CURRENT field received from the
     *      previous day (a "00:00,HH:MM" period where HH:MM ≤ 06:00).
     *   2. Look at the NEXT day field.  If it carries an overnight prefix that
     *      was written by this field's last save, reconstruct the original
     *      overnight value so the "(next day)" badge renders correctly.
     */
    const getFieldDisplayValue = (field: TemplateFormFieldModel): string | undefined => {
        const rawValue = getDisplayValue(field);

        // 1. Strip this field's own overnight prefix (belongs to previous day).
        //    Pass the previous day's raw value so the heuristic only strips the
        //    prefix when the previous day actually has an overnight range (23:59).
        const prevField = getPreviousDaySiblingField(field);
        const prevFieldRaw = prevField ? getDisplayValue(prevField) : undefined;
        const { prefix: ownPrefix, remainder: cleanValue } = extractOvernightPrefix(rawValue, prevFieldRaw);

        // 2. Look at the next day and extract its overnight prefix.
        //    Pass the current field's clean value as context so the heuristic only
        //    recognises the prefix when this field has an overnight range (23:59).
        const nextField = getNextDaySiblingField(field);
        if (nextField) {
            const nextRaw = getDisplayValue(nextField);
            const { prefix: nextPrefix } = extractOvernightPrefix(nextRaw, cleanValue);
            if (nextPrefix && cleanValue && cleanValue.includes('23:59')) {
                return reconstructOvernightDisplay(cleanValue, nextPrefix);
            }
        }

        // Return cleaned value (own overnight prefix stripped if present)
        return cleanValue || undefined;
    };

    const handleCopyToSiblingDays = (sourceField: TemplateFormFieldModel, targetLabels: string[], valueToApply: string) => {
        const siblings = getSingleDayTimeRangeSiblings(sourceField);
        const targets = siblings.filter(f => targetLabels.includes(f.title ?? f.label ?? ''));
        if (targets.length === 0 || !valueToApply) return;

        const { currentDay, nextDay } = splitOvernightValue(valueToApply);
        const allUpdates: Array<{ field: TemplateFormFieldModel; value: string }> = [];

        // Build a set of target field identifiers so we can avoid conflicting updates.
        const targetIds = new Set(targets.map(f => f.id ?? f.name));

        for (const targetField of targets) {
            // When copying overnight hours every target must carry the midnight overflow
            // prefix so that the day before it can reconstruct its overnight display
            // value. Without the prefix embedded here, a subsequent loop iteration
            // for the next contiguous target would overwrite the prefix that was
            // written as a "nextField" update for the previous target.
            const targetValue =
                nextDay !== null
                    ? mergeOvernightPrefix(nextDay, currentDay)
                    : currentDay;

            allUpdates.push({ field: targetField, value: targetValue });

            // Only write the nextField overflow update when the next sibling is NOT
            // itself a copy target — if it is a target, its own loop iteration above
            // already embeds the prefix correctly, and a duplicate update here would
            // overwrite it with a stale getDisplayValue snapshot.
            if (nextDay !== null) {
                const nextField = getNextDaySiblingField(targetField);
                if (nextField && !targetIds.has(nextField.id ?? nextField.name)) {
                    const mergedValue = mergeOvernightPrefix(nextDay, getDisplayValue(nextField));
                    allUpdates.push({ field: nextField, value: mergedValue });
                }
            }
        }

        if (props.onMultipleFieldValuesChange) {
            props.onMultipleFieldValuesChange(allUpdates);
        } else {
            allUpdates.forEach(({ field, value }) => props.onFieldValueChange(field, value));
        }
    };

    const renderSingleDayTimeRange = (field: TemplateFormFieldModel) => {
        const siblings = getSingleDayTimeRangeSiblings(field);
        const siblingDayLabels = siblings.map(f => f.title ?? f.label ?? '').filter(Boolean);
        return (
            <SingleDayTimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getFieldDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                required={field.isRequired}
                onChange={(value) => handleSingleDayTimeRangeChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
                siblingDayLabels={siblingDayLabels}
                onCopyToOtherDays={(targetLabels, valueToApply) => handleCopyToSiblingDays(field, targetLabels, valueToApply)}
            />
        )
    };

    const renderSpecialDate = (field: TemplateFormFieldModel) => {
        return (
            <SpecialDateField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        )
    };

    const renderSetupProperty = (field: TemplateFormFieldModel) => {
        switch (field.fieldType) {
            case "ShortTextField":
                return renderShortText(field);
            case "NumberTextField":
                return renderShortText(field, 'number');
            case "DateTextField":
                return renderShortText(field, 'date');
            case "PhoneNumberField":
                return renderPhoneNumber(field);
            case "LongTextField":
                return renderLongText(field);
            case "SwitchField":
                return renderSwitch(field);
            case "SelectField":
                return renderSelect(field);
            case "CheckboxGroupField":
                return renderCheckboxGroup(field);
            case "RadioGroupField":
                return renderRadioGroup(field);
            case "CheckboxField":
                return renderCheckbox(field);
            case "FileUploadField":
                return renderFileUpload(field);
            case "TimeRangeField":
                return renderTimeRange(field);
            case "SingleDayTimeRangeField":
                return renderSingleDayTimeRange(field);
            case "SpecialDateField":
                return renderSpecialDate(field);
            case "SSMLField":
            case "SSMLTextField":
                return renderSsmlField(field);
        }
    };

    const renderFields = () => {
        // Use the consolidated helper function to get visible fields
        // This applies both associatedVariable and admin field filtering
        const fields = getVisibleFields(
            props.section,
            props.showAdminFields ?? true,
            props.values
        );

        return (
            <div className={setupSectionContainer}>
                <h3>{props.section.title}</h3>
                {fields.map((p, subIdx) => {
                    const fieldSupportsVariations = supportsVariations(p);
                    return (
                        <div key={p.id ?? `setupSection_${subIdx}`} id={p.id ?? p.name} className={setupPropertyContainer}>
                            <FormFieldCard
                                title={p.title}
                                tip={p.tip}
                                required={p.isRequired}
                                isRecentlyAddedModuleFormField={isRecentlyAddedModuleFormField(p)}
                                hasVariations={fieldSupportsVariations ? hasFieldVariations(p) : false}
                                onSaveVariation={fieldSupportsVariations ? () => handleOpenSaveVariation(p) : undefined}
                                onViewVariations={fieldSupportsVariations ? () => handleOpenViewVariations(p) : undefined}
                            >
                                {renderSetupProperty(p)}
                            </FormFieldCard>
                        </div>
                    )
                })}
            </div>
        )
    };

    const isLoading = props.appliedAppTemplateContainer.state.isLoading || props.appliedAppTemplateContainer.state.isSaving;

    const handleSaveForm = () => {
        setShowSaveChangesModal(true);
    };

    const performSave = async () => {
        await props.appliedAppTemplateContainer.saveTemplateFields(
            props.applicationId,
            props.appliedAppTemplate.id,
            props.templateFormValues || props.values || [],
            {
                finalize: false,
                useModifiedFieldsOnly: true
            }
        );
    };

    const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    return (
        <>
            <ScrollablePanelBody autoHeight={true} autoHeightMax={"100vh - 136px"}>
                <SectionFormWrapper>
                    <PanelContainer className={panelContainerStyle}>
                        <PanelWrapper>
                            {renderFields()}
                            <ButtonWrapper>
                                {/* These are the buttons in the bottom left of the Spark form section */}
                                <SaveButton
                                    loading={isLoading}
                                    disabled={props.hasValidationErrors}
                                    themes={["secondary"]}
                                    text="Save"
                                    type="button"
                                    onClick={handleSaveForm}
                                />
                                <NextButton
                                    disabled={
                                        props.hasValidationErrors ||
                                        (props.appliedAppTemplate?.templateForm?.templateConfiguration?.dynamicFormUrl != null &&
                                        props.combinedRequiredFields.length != props.completeFields.length &&
                                        !isLoading)
                                    }
                                    loading={isLoading}
                                    themes={["primary"]}
                                    text="Next"
                                    type="button"
                                    onClick={props.submitForm}
                                />
                            </ButtonWrapper>
                        </PanelWrapper>
                        {props.section.instructionsMarkdown && (
                            <RightPanelWrapper>
                                <TemplateInstructionsPanel
                                    title="Additional Instructions"
                                    instructionsMarkdown={props.section.instructionsMarkdown}
                                />
                            </RightPanelWrapper>
                        )}
                    </PanelContainer>
                </SectionFormWrapper>
            </ScrollablePanelBody>
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveChangesModal}
                closeModal={closeSaveChangesModal}
                changedFields={props.appliedAppTemplateContainer.getChangedFieldsForDisplay(
                    props.appliedAppTemplate.id,
                    props.templateFormValues || props.values || []
                )}
                submitChanges={performSave}
                isLoading={props.appliedAppTemplateContainer.state.isSaving}
                hasErrors={props.hasValidationErrors}
            />
            {/* Variation Modals */}
            {selectedField && (
                <>
                    <SaveFieldVariationModal
                        isVisible={saveVariationModalVisible}
                        fieldTitle={selectedField.title}
                        fieldValue={getValue(selectedField) || ''}
                        onClose={() => {
                            setSaveVariationModalVisible(false);
                            setSelectedField(null);
                        }}
                        applicationId={props.applicationId}
                    />
                    <ViewFieldVariationsModal
                        isVisible={viewVariationsModalVisible}
                        fieldTitle={selectedField.title}
                        variations={getFieldVariations(selectedField)}
                        onClose={() => {
                            setViewVariationsModalVisible(false);
                            setSelectedField(null);
                        }}
                        onSelectVariation={handleSelectVariation}
                    />
                </>
            )}
        </>
    );
};

const SectionFormWrapper = styled.div`
    display: flex;
`;

const PanelWrapper = styled.div`
    flex: row;
    flex: 1;
`;

const RightPanelWrapper = styled.div`
    flex: row;
    flex: 1;
    ${breakpoint_small} {
        display: none;
    }
`;

const NextButton = styled(Button)`
    margin-top: 0px;
    margin-left: 0px;
`;

const SaveButton = styled(Button)`
    margin-top: 0px;
`;

const panelContainerStyle = css`
    display: flex;
`;

const selectField = css`
    padding-top: 16px;
  `;

const checkboxField = css`
    padding-left: 16px;
    .tooltip {
      margin-top: 3px;
    }
  `;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    margin: 0 0 32px 18px;
    ${breakpoint_small} {
      justify-content: flex-end;
    }
  `;

const setupSectionContainer = css`
    padding: 32px;
    h3 {
      margin-bottom: 16px;
      font-style: normal;
      font-weight: normal;
      font-size: 18px;
      line-height: 24px;
    }
    ${breakpoint_small} {
      padding: 16px;
      h3 {
        margin-bottom: 0px;
      }
    }
  `;

const label = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    text-align: left;
    margin: 8px 16px;
  `;

const switchContainer = css`
    margin-bottom: 32px;
  `;

const setupPropertyContainer = css`
    padding: 16px 0px;
    position: relative;
    .field-triangle {
      position: absolute;
      width: 24px;
      height: 24px;
      background: ${color_shades_lighter};
      right: -44px;
      top: 64px;
      transform: rotate(45deg);
      border-left: 1px solid ${color_shades_dark};
      border-bottom: 1px solid ${color_shades_dark};
    }
  `;

export default AppliedApplicationTemplateSectionForm;

